import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';
import PrintErrorMessage from '@/components/organisms/feedback/PrintErrorMessage';

// Ordre complet du workflow d'onboarding
const STEP_FLOW = [
    'PERSONAL_INFO',
    'DOCUMENTS',
    'CURRENT_MISSION',
    'SYNAPSE_VALIDATION',
    'ADMINISTRATIVE_INFO',
    'CONTRACT_GENERATION',
    'OFFICE_APPOINTMENT',
    'COMPLETE',
];

// Etapes reinitialisables par l'admin (enum backend ResetCandidateStep).
// SYNAPSE_VALIDATION et CONTRACT_GENERATION sont des etapes d'attente cote admin : on les saute.
const RESETTABLE_STEPS = [
    'PERSONAL_INFO',
    'DOCUMENTS',
    'CURRENT_MISSION',
    'ADMINISTRATIVE_INFO',
    'OFFICE_APPOINTMENT',
];

const getPreviousStep = (currentStep) => {
    // Cas particulier: ADMINISTRATIVE_INFO revient à CURRENT_MISSION
    if (currentStep === 'ADMINISTRATIVE_INFO') {
        return 'CURRENT_MISSION';
    }

    // Cas particulier: OFFICE_APPOINTMENT revient à ADMINISTRATIVE_INFO
    if (currentStep === 'OFFICE_APPOINTMENT') {
        return 'ADMINISTRATIVE_INFO';
    }

    const idx = STEP_FLOW.indexOf(currentStep);
    if (idx <= 0) return null;
    return STEP_FLOW[idx - 1];
};

const ResetStepDialog = ({ open, onOpenChange, candidateStep, onConfirm, loading, error }) => {
    const [t] = useTranslation('global');
    const [comment, setComment] = useState('');

    const targetStep = getPreviousStep(candidateStep);

    const handleOpenChange = (nextOpen) => {
        if (!nextOpen) setComment('');
        onOpenChange(nextOpen);
    };

    const handleConfirm = async () => {
        if (!targetStep) return;
        await onConfirm(targetStep, comment.trim() || null);
        setComment('');
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        <Label message="management.onboarding.resetDialog.title" size="h4" />
                    </DialogTitle>
                    <DialogDescription className="flex flex-col gap-4 py-4">

                        <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center gap-2">
                                <Label message="management.onboarding.resetDialog.currentStepLabel" />
                                {candidateStep ? (
                                    <Label fontWeight="bold">
                                        {t(`management.onboarding.step.${candidateStep}`)}
                                    </Label>
                                ) : (
                                    <Label color="gray400" message="error.unfilled" />
                                )}
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <Label message="management.onboarding.resetDialog.targetStepLabel" />
                                {targetStep ? (
                                    <Label fontWeight="bold">
                                        {t(`management.onboarding.step.${targetStep}`)}
                                    </Label>
                                ) : (
                                    <Label color="error" message="management.onboarding.resetDialog.noPreviousStep" />
                                )}
                            </span>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label message="management.onboarding.resetDialog.commentLabel" fontWeight="semibold" />
                            <Textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder={t('management.onboarding.resetDialog.commentPlaceholder')}
                                rows={3}
                                maxLength={500}
                                className="resize-none"
                            />
                            <Label size="small" color="grey" className="self-end">
                                {comment.length}/500
                            </Label>
                        </div>

                        {error && <PrintErrorMessage error={error} />}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="sm:justify-start">
                    <Button
                        type="button"
                        style="ghost"
                        text="global.actions.cancel"
                        onClick={() => handleOpenChange(false)}
                    />
                    <div className="flex items-center justify-center">
                        {loading && <span className="loader" />}
                    </div>
                    <Button
                        style="fill"
                        text="management.onboarding.resetDialog.confirm"
                        className="w-full"
                        onClick={handleConfirm}
                        disabled={!targetStep || loading}
                    />
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ResetStepDialog;
